import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES, Document, Block, NodeData } from "@contentful/rich-text-types"
import { css, cx } from "emotion"
import Img, { FluidObject } from "gatsby-image"
import React from "react"
import { FootnoteOverlay } from "../components/footnoteOverlay"
import SEO from "../components/seo";

import { Breakpoints, Cell, colors, Grid, spacing, typography } from "@avonova-noa/common"

import { ArticlePreview, Post } from "./../components/articlePreview"
import { Button, skins } from "@avonova-noa/components"
import GalleryComponent from "./../components/galleryComponent"
import Textbox from "./../components/textbox"
import ImageAsset from "./../components/imageAsset"
import Layout from "./../components/layout"
import { NextPrevArticle } from "./../components/nextPrevArticle"
import ReadingProgress from "./../components/readingProgress"
import { TypeLabel, typeVariations } from "../components/typeLabel"

const endBlob = require("../images/endBlob.svg") as string;

const fullWidthImage = css`
  left: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  max-width: 100vw;
  width: 100vw;
  height: calc(100vh - 60px); 
  overflow: hidden;
  position: relative;
  right: 50%;
  .gatsby-image-wrapper {
    height: 100%;
  }
`
const heading = css`
 ${typography.subtitle}
 display: inline;
 margin-right: 0.2em;
 font-variant-caps: small-caps;
 
 `

const imageOverlay = css`
&:after{
  content: "";
  background: #000;
  opacity: 0.4;
  top: 0;
  left:0;
  right: 0;
  bottom: 0;
  position: absolute;
}
 
 `

const fullWidthImageContent = css`
  align-items: center;
  color: #fff;
  display: flex;
  min-height: 100%;
  position: absolute;
  text-align: center;
  top: 0;
  width: 100%;
  z-index: 10;
`

const title = css`
 ${typography.h1}
 color: #fff;
 ${typography.breakWord}
`
const date = css`
 ${typography.overline}
 display: block;
 text-shadow: 0px 2px 2px rgba(0,0,0,0.5);
  margin-bottom: ${spacing.xl};
  @media (${Breakpoints.mobile}) {
    margin-bottom: ${spacing.ms};
  }
`;

const intro = css`
  ${typography.h5}
  text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  margin-bottom: ${spacing.m};
  @media (${Breakpoints.mobile}) {
    margin-bottom: ${spacing.ms};
  }
`

const bodyText = css`
  .gatsby-resp-image-figcaption {
    font-family: ${typography.sansSerif};
    font-size: 14px;
    text-align: center;
    padding-top: 32px;
  }
  blockquote {
   ${typography.quote}
    text-align: center;
    margin-left: calc(-920px / 2 + (920px / 2) / 2);
    margin-right: calc(-920px / 2 + (920px / 2) / 2);
    @media (${Breakpoints.tablet}) {
      margin: 2.5em 0;
    }
  }
  a {
    font-weight: bold;
    border-bottom: 1px solid #27ddbb;
    color: ${colors.green.green11};
    text-decoration: none;
  }
  p {
    color: ${colors.green.green11};
  }
 
  counter-reset: footnotes footnotesNumber;

  .footnoteText {
    padding-right: 0.8em;
    position: relative;
    text-decoration: underline;
  }

  .footnoteText::after {
    counter-increment: footnotes;
    content: "" counter(footnotes) "";
    color: #fc4b47;
    font-size: smaller;
    position: absolute;
    margin-top: -0.5em;
  }

  .sidenote{
    display: block;
    font-family: Vollkorn;
    font-size: 14px;
    font-style: italic;
    line-height: 20px;
    width: 230px;
    right: -280px;
    margin-bottom: ${spacing.ms};
    &::before {
      border-bottom: 1px solid #333;
      color: #fc4b47;
      content: "" counter(footnotesNumber) "";
      counter-increment: footnotesNumber;
      display: block;
      font-style: normal;
      margin: 0 0 0.5em;
      padding: 0.5em 0 0;
    }

    @media (${Breakpoints.tablet}) {
      display: none;
    }
  }
`

const nextPrevArticleWrapper = css`
  margin-top: ${spacing.enormous};
  overflow: hidden;

  @media (${Breakpoints.mobile}) {
    margin-top: ${spacing.xxl};
  }
`
const relatedArticlesWrapper = css`
  margin-top: ${spacing.xxl};

  h3{
    ${typography.h4}
    text-align: center;
    padding: 0 16px;
    margin-bottom: ${spacing.ml};

    @media (${Breakpoints.tablet}) {
      margin-top: ${spacing.xxl};
      ${typography.h6}
    }
  }

  @media (${Breakpoints.mobile}) {
    margin-top: ${spacing.ml};

    h3 {
      margin-bottom: ${spacing.m};
    }
  }
`

const paragraphCSS = css`
  display: inline;
`

const paragraphCell = css`
${typography.bodySerif}
  position: relative;
  margin-bottom: ${spacing.m};

  @media (${Breakpoints.mobile}) {
    margin-bottom: ${spacing.ms};
  }
`

const brushStroke = css`
  display:block; 
  margin: ${spacing.xl} auto 0;
  height: 60px; 
  @media (${Breakpoints.tablet}){
    margin-top: ${spacing.s}; 
    width: 60px;
}`

interface BlogPost {
  slug: string
  tags: string[]
  title: string
  body: {
    children: {
      html: string
      timeToRead: string
    }[]
  }
  description: string
  postcontent: {
    content: {
      childContentfulRichText: {
        timeToRead: string
      }
      json: {
        content: NodeData
      }
    }

  }
  author: {
    name: string
  }
  richcontent: {
    content: {
      id: string
      content: string
      json: string
    }
  }
  createdAt: string
  type: keyof typeof typeVariations,
  category: {
    name: string
  }
  heroImage: {
    fluid: FluidObject
  }
  next: {
    title: string
    slug: string
  }
  previous: {
    title: string
    slug: string
  }
  relatedArticles: Post[]
}

const options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: Block) => {

      return node.content.map(item => {
        const ref = React.createRef()
        return item.nodeType === INLINES.ENTRY_HYPERLINK ? (

          <FootnoteOverlay item={item} key="footnote" value={item.data.target.fields.content["nb-NO"]} />

        ) : item.nodeType === INLINES.HYPERLINK ?
            (<a href={item.data.uri}>
              {item.content.map((i) => {
                return i.nodeType === "text" && i.value;
              })}

            </a>) :
            (
              <p className={paragraphCSS}>
                { item.nodeType === "text" && item.value}
              </p>
            )
      })
    },
    [BLOCKS.HEADING_2]: (node: Block) => {
      return <h2 className={heading}>{node.content.map(content => {

        return content.nodeType === 'text' && content.value
      })}
      </h2>
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: Block) => {
      const type = node.data.target.sys.contentType.sys.contentful_id
      if (type === "gallery") {
        const fields = node.data.target.fields
        const imageUrl = fields.image && fields.image["nb-NO"].fields.file["nb-NO"].url
        const content = fields.content && fields.content["nb-NO"]
        const view = fields.viewType && fields.viewType["nb-NO"]
        const title = fields.title && fields.title["nb-NO"]
        return <GalleryComponent content={content} imageUrl={imageUrl} viewType={view} title={title} />
      } else if (type === "textbox") {
        const content = node.data.target.fields.content["nb-NO"]
        return <Textbox content={content} />
      }
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: Block) => {
      return (
        <ImageAsset
          url={node.data.target.fields.file["nb-NO"].url}
          description={node.data.target.fields.description && node.data.target.fields.description["nb-NO"]}
        />
      )
    },
  },
}

function isTextbox(item: Block) {
  return item.nodeType === "embedded-entry-block" &&
    item.data.target.sys.contentType.sys.id === "textbox"
}

function isParagraph(item: Block) {
  return item.nodeType === "paragraph"
}

function BlogPost({ pageContext }: { pageContext: BlogPost }) {
  return (
    <Layout>
      <SEO article={true} title={`${pageContext.title} | Helsehjulet`} description={pageContext.description} url={`https://helsehjulet.com/${pageContext.slug}`} image={pageContext.heroImage && pageContext.heroImage.fluid.src} />
      <div
        className={cx(fullWidthImage, imageOverlay)}
      >
        {pageContext.heroImage &&
          <Img
            alt={""}
            fluid={pageContext.heroImage.fluid}
          />
        }
        <div className={fullWidthImageContent}>
          <Grid gap="0">
            <Cell width={12}  >
              {!!pageContext.createdAt && (
                <span className={date}>
                  {pageContext.createdAt}
                </span>
              )}

              {pageContext.title &&
                <h1 className={title}>{pageContext.title}</h1>}
            </Cell>
            <Cell width={6} left={4} mobile={{ width: 12, left: 1 }}>
              {pageContext.description &&
                <div
                  className={intro}
                  dangerouslySetInnerHTML={{
                    __html: pageContext.description,
                  }}
                />}
              {!!pageContext.author && (
                <span className={`${typography.overline} `}>
                  Skrevet av {pageContext.author.name}
                </span>
              )}
            </Cell>
          </Grid>
        </div>
      </div>
      <ReadingProgress />

      <div className={css`margin: ${spacing.xxl} 0;`}>
        <Grid columns={12} mobile={{ columns: 12, gap: "0" }} tablet={{ gap: "0" }}>
          {/* <Cell width={3} tablet={{ width: 10, left: 2, customCss: css`text-align: center;`, top: 3 }}> </Cell> Placeholder for share buttons */}
          <Cell width={4} left={4} tablet={{ width: 10, left: 2, customCss: css`text-align: center;` }}>
            {pageContext.type && (
              <TypeLabel
                type={pageContext.type}
                customCss={css`position: static;  margin-right: ${spacing.ms}; @media (${Breakpoints.tablet}){  width: fit-content; margin: 0 auto ${spacing.ms}; display: block; transform: none; } `}
              />
            )}
            {pageContext.category &&
              <Button
                as="link"
                to=""
                skin={skins.underlined}
                buttonText={pageContext.category.name}
                customCss={css`
                padding: 0 !important;
                text-transform: uppercase;
                font-size: 12px;
                @media (${Breakpoints.tablet}){
                  margin-bottom: ${spacing.xxs};
                }`}
              />}
          </Cell>
          <Cell width={2} tablet={{ width: 10, left: 2, customCss: css`text-align: center;` }} >{pageContext.postcontent && pageContext.postcontent.content ? (
            pageContext.postcontent.content.childContentfulRichText ? (
              <span className={css`${typography.overline} 
              text-align: right;
              display: block; 
              text-transform: none; 
              padding: 8px 0;
              line-height: 14px;
              color: ${colors.gray.dark1};
              @media (${Breakpoints.tablet}){
                text-align: center;
              }
              `}>
                Lesetid:{" "}
                {
                  pageContext.postcontent.content.childContentfulRichText
                    .timeToRead
                }{" "}
                    minutter
              </span>
            ) : null
          ) : null}</Cell>
        </Grid>
      </div>

      <div className={bodyText}>
        <Grid columns={12} gap="0 50px" mobile={{ gap: "0" }} tablet={{ gap: "0" }}>
          {pageContext.postcontent && pageContext.postcontent.content &&
            pageContext.postcontent.content.json.content.map((item: Document, index: number) => {
              let content = pageContext.postcontent.content.json.content
              let h2 = null;
            //   console.log("content", content)
              if (content[index - 1] && content[index - 1].nodeType === BLOCKS.HEADING_2) {
                h2 = documentToReactComponents(content[index - 1], options)
              }

              const componentToRender = documentToReactComponents(item, options)
              let footnoteValues: React.ReactElement[] = [];
              componentToRender && componentToRender.length && componentToRender.map(item => {

                if (item.key === "footnote") {
                  footnoteValues.push(item.props.value)
                }
                return null
              })

              if (componentToRender && componentToRender.type !== "h2") {
                return (
                  <>
                    <Cell
                      mobile={{
                        left: 1,
                        width: 12,
                        customCss: isParagraph(item) ? paragraphCell : ""
                      }}
                      tablet={{ left: 2, width: 10, customCss: isParagraph(item) ? paragraphCell : "" }}

                      left={isTextbox(item) ? 1 : 4}
                      width={isTextbox(item) ? 3 : 6}
                      height={isTextbox(item) ? 4 : 1}
                      customCss={isParagraph(item) ? paragraphCell : ""}
                    >
                      {!!h2 && h2}
                      {componentToRender}
                    </Cell>
                    {
                      !!footnoteValues.length &&
                      <Cell left={10} customCss={css`position:relative;`} mobile={{ left: 1, width: 12 }}
                        tablet={{ left: 2, width: 10 }}>
                        {footnoteValues.map((footnote) => {
                          return <div className="sidenote">{footnote}</div>
                        })}
                      </Cell>
                    }

                  </>
                )
              }
            }
            )}
          {!!pageContext.tags &&
            <Cell left={4} width={6} tablet={{ width: 6 }} mobile={{ width: 12, left: 1 }} customCss={css` margin-top: ${spacing.xl};`}>
              <span className={typography.subtitle}>Les mer om: </span>
              {pageContext.tags.map(item => (
                <strong className={typography.subtitle}
                  key={"tag_" + item}
                >{`${item}, `}</strong>
              ))}
            </Cell>
          }
        </Grid>
        <img className={brushStroke} src={endBlob} />
      </div>
      {
        !!pageContext.relatedArticles &&
        <div className={relatedArticlesWrapper}>
          <h3>Sjekk også ut</h3>
          <Grid mobile={{ gap: "40px 0" }}>
            {pageContext.relatedArticles.map(article => {
              return (
                <Cell width={3} tablet={{ width: 6 }} mobile={{ width: 12 }}>
                  <ArticlePreview
                    post={article}
                    size="1/4"
                  ></ArticlePreview>
                </Cell>
              )
            })}
          </Grid>
        </div>
      }
      <div className={nextPrevArticleWrapper}>
        <Grid columns={pageContext.previous && pageContext.next ? 2 : 1} mobile={{ columns: 1, gap: "0" }} gap="0" maxWidth="none" customCss={css`padding: 0;`}>
          {pageContext.previous && <Cell>
            <NextPrevArticle
              article={pageContext.previous}
              direction="prev"
            ></NextPrevArticle>
          </Cell>}
          {pageContext.next &&
            <Cell>
              <NextPrevArticle
                article={pageContext.next}
                direction="next"
              ></NextPrevArticle>
            </Cell>
          }
        </Grid>
      </div>
    </Layout >
  )
}

export default BlogPost
